/**
 * The price derivation of one Activity event, as data (CU-869fkdhjv phase 3, plan § 2.4 and phase 5.1).
 *
 * `DerivationRail` and `TetherDerivation` ("How this was calculated", the detail's "Price derivation") used to
 * compose their lines inside the JSX. The composition lives here now, unchanged, so the rail renders from
 * `derivationSteps(row)` and "Ask Iris" sends the very same lines (T17): one source, no copy. Each step is
 *   - `{ type: 'node', label, amount, tone, notes[], last? }` — a milestone of the rail (`RailNode`);
 *   - `{ type: 'step', operator, note }` — the arithmetic between two (`RailStep`);
 *   - `{ type: 'result', amount, was, delta, simulated, unwritten }` — the market rail's last node; `unwritten` is
 *     the "<resolution> — nothing written" note when the server knows no write landed (`./pushWording`), else null.
 * The cap vocabulary (`CAP_LABELS`, `capLabel`, `capNotes`), `eur`, `totalOf` and `policyCaption` moved here
 * verbatim from `Activity/index.jsx`, which imports them back.
 *
 * Pure and alias-free (TL R4): relative imports only.
 */
import { capKey, protectionNotes } from './dateProtection.js'
import { consistencyNotes } from './consistencyCap.js'
import { conversionFactor, conversionNote } from './markupUsed.js'
import { incompleteMarketCopy } from './incompleteMarket.js'
import { overridable } from './override.js'
import { unwrittenNote } from './pushWording.js'
import { dimensionLabel, tetherSideLabel } from '../../Setup/@services/tethers.js'

//: Caps that act on the shelf price, before the markup is removed. The
//: rest act after. The distinction is not cosmetic: putting a PVP-stage
//: cap below the conversion would show a euro amount that never existed
//: at that point in the chain. `price_limit` joined this set on
//: 2026-10-05 (CU-869fbzw60): the floors are shelf prices and the engine
//: now applies them before the conversion, so the rail has to draw them
//: there — or the one sum the operator can check by hand stops adding up.
export const PVP_STAGE_CAPS = new Set([
	'rank_cap', 'keep_position',
	'never_below_competitor', 'never_above_competitor',
	'price_limit',
])

export const CAP_LABELS = {
	rank_cap:               'Rank cap',
	keep_position:          'Keep-position guard',
	never_below_competitor: 'Never below competitor',
	never_above_competitor: 'Never above competitor',
	markup_cap:             'Markup limit',
	price_limit:            'Price limit',
	date_protection:        'Date protection',
	category_consistency:   'Category consistency',
}

//: A cap's label comes from its key, not its reason: a date protection
//: keeps `reason = price_limit` and says what it is in `source.kind`
//: (CU-869fe8jrk). One function, so row, rail, Limits and filter agree.
export function capLabel(cap) {
	return CAP_LABELS[capKey(cap)] || cap?.reason
}

export const POLICY_LABELS = { ranking: 'Ranking', market_trend: 'Market trend' }

/**
 * What a cap has to say for itself, beyond the two euro amounts.
 *
 * A node that reads "Markup limit · 23.56 € → 23.67 €" says a limit
 * fired and not which limit, which is the thing an operator wants and
 * the thing they would otherwise have to go and look up (Curro,
 * 2026-09-28). The engine already records it in the cap's `source`.
 */
export function capNotes(cap, row) {
	if (capKey(cap) === 'date_protection') return protectionNotes(cap, row)
	const src = cap?.source || {}
	const out = []
	if (cap?.reason === 'markup_cap') {
		if (src.markup_limit_pct != null) {
			out.push(row?.product?.name
				? `the product row caps this site at ${Number(src.markup_limit_pct).toFixed(2)} %`
				: `rate card allows ${Number(src.markup_limit_pct).toFixed(2)} % markup`)
		}
		if (src.broker_markup_pct != null) {
			out.push(`the market priced at ${Number(src.broker_markup_pct).toFixed(2)} %`)
		}
	}
	if (cap?.reason === 'category_consistency') {
		return consistencyNotes(cap, row?.scope?.duration_days)
	}
	if (cap?.reason === 'price_limit') {
		// The direction is not stored, but it is in the numbers: a cap
		// that raised the price was a floor, one that lowered it a
		// ceiling. Stating it beats making the reader compare.
		const raised = Number(cap.to) > Number(cap.from)
		const value = src.limit_value ?? cap.to
		out.push(`${raised ? 'minimum' : 'maximum'} ${eur(value)}${src.label ? ` · ${src.label}` : ''}`)
		if (src.price_limit_id) out.push(`limit ${src.price_limit_id}`)
	}
	return out
}

export function eur(n) {
	return n == null ? '—' : `${Number(n).toFixed(2)} €`
}

/** How the policy is described in one line, next to the title. */
export function policyCaption(d) {
	if (!d) return null
	const bits = [POLICY_LABELS[d.policy] || d.policy]
	if (d.params?.targetPosition) bits.push(`target position ${d.params.targetPosition}`)
	if (d.params?.rankCap) bits.push(`rank cap ${d.params.rankCap}`)
	const u = d.params?.undercut
	if (u?.value != null) {
		const unit = Number(u.unit) === 0 ? '€' : '%'
		bits.push(`undercut ${u.value}${unit} ${Number(u.direction) === 0 ? 'down' : 'up'}`)
	}
	return bits.join(' · ')
}

/**
 * Totals, not daily rates.
 *
 * The engine decides in daily terms — that is the currency of the
 * comparison — but an operator quotes a rental, so every figure here
 * is the total for the stay (Curro, 2026-09-28). The exception is the
 * final push: the PMS stores the daily rate, and that is the number
 * somebody will check against Ranteon, so it shows both.
 *
 * Where the rate shop gave us its own total it is used verbatim;
 * `daily_price` is rounded to the cent, so `daily × days` drifts by a
 * cent or two from what the market actually displayed.
 */
export function totalOf(daily, days) {
	if (daily == null) return null
	const n = Number(days)
	return n > 0 ? Number(daily) * n : Number(daily)
}

// `HH:mm` of an ISO time in local time, as the page's `hhmm` (date-fns `format(d, 'HH:mm')`)
function hhmm(iso) {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ''
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * The market rail of a decision, or null when the row has nothing to derive (`DerivationRail` draws nothing).
 * `{ kind: 'market', caption, steps, predates }` — `predates`: the run predates the market trail.
 */
export function derivationSteps(row) {
	if (!row) return null
	// Totals, like the sections above it (see `totalOf`)
	const days = row.scope?.duration_days
	const tot = (v) => totalOf(v, days)
	const d = row.derivation
	const markup = row.broker_markup_pct
	const caps = row.cap_chain || []
	const pvpCaps = caps.filter(c => PVP_STAGE_CAPS.has(c.reason))
	const netCaps = caps.filter(c => !PVP_STAGE_CAPS.has(c.reason))

	const target = row.proposed_price
	const result = row.new_price ?? row.pushed_price
	// The net the conversion produced, before any cap of its own. The
	// first net-stage cap knows it exactly; with none, it is the result.
	const netBeforeCaps = netCaps.length ? netCaps[0].from : result

	if (!d && target == null && result == null) return null

	// Which percentage the engine actually divided by — not always the
	// one it measured (CU-869ffwfam).
	const factor = conversionFactor(markup, d?.limits?.markup)
	const caption = policyCaption(d)
	// The server does not send a delta; `DeltaCell` derives it the same
	// way, and the two must not disagree on the same screen.
	const delta = row.delta ?? (result == null || row.previous_price == null
		? null : result - row.previous_price)
	// How far out of the market we were.
	const versusMarket = (d?.market_low && row.market_pvp)
		? Number(row.market_pvp) / Number(d.market_low) : null

	//: `null` on every ordinary decision, so the two steps below are
	//: drawn only where they say something.
	const imCopy = incompleteMarketCopy(row, days)

	// Numbered as they are rendered, so a row missing the market stage
	// starts at one rather than at two.
	let step = 0
	const n = () => ++step
	const steps = []
	const capNode = (c, withOverride) => {
		const notes = [...capNotes(c, row)]
		// The override is a note under the cap it jumped, never a rewrite of the rail (UX decision 8, CU-869fdg4nu)
		if (withOverride && row.resolution === 'manual_override' && overridable([c]).can) {
			notes.push(`overridden${row.resolution_actor ? ` by ${row.resolution_actor}` : ''}${row.resolution_at ? ` · ${hhmm(row.resolution_at)}` : ''} — this cap was jumped for this one hit`)
		}
		return { type: 'node', tone: 'amber', label: capLabel(c), amount: `${eur(tot(c.from))} → ${eur(tot(c.to))}`, notes }
	}

	// 1 — the market ("cheapest" only when it is: an incomplete market anchors on the dearest, UX gate 2026-10-05)
	if (d && d.reference != null) {
		const notes = [`${d.reference_provider || 'the market'}${d.competitors_in_scope != null
			? ` — ${d.incomplete_market ? 'dearest' : 'cheapest'} of ${d.competitors_in_scope} competitor${d.competitors_in_scope === 1 ? '' : 's'} in scope`
			: ''}`]
		if (imCopy) notes.push(imCopy.rail.node1Note)
		if (d.market_low != null && d.market_high != null) notes.push(`spread ${eur(tot(d.market_low))} … ${eur(tot(d.market_high))}`)
		steps.push({ type: 'node', label: `${n()} · The market`, amount: eur(tot(d.reference)), tone: 'neutral', notes })
	}

	// 2 — our own price, and the markup it implies
	if (row.market_pvp != null) {
		const notes = [`shelf price in this rate shop${versusMarket ? ` · ${versusMarket.toFixed(1)}× the cheapest` : ''}`]
		if (row.read_net != null) notes.push(`price in the PMS ${eur(tot(row.read_net))}${markup != null ? ` — broker markup ${Number(markup).toFixed(2)} %` : ''}`)
		steps.push({ type: 'node', label: `${n()} · Our price today`, amount: eur(tot(row.market_pvp)), tone: 'neutral', notes })
	}

	// 3 — what the rule decides, with the undercut on the way in
	for (const s of (d?.steps || []).filter(s => s.step === 'undercut' && s.from !== s.to)) {
		steps.push({ type: 'step', operator: `undercut ${eur(tot(s.from))} → ${eur(tot(s.to))}`, note: s.against ? `against ${d.reference_provider || s.against}` : null })
	}

	// 3b — the incomplete-market branch, when it priced (replaces the undercut step)
	if (imCopy) steps.push({ type: 'step', operator: imCopy.rail.step, note: imCopy.rail.stepNote })

	for (const c of pvpCaps) steps.push(capNode(c, true))

	steps.push({ type: 'node', label: `${n()} · The strategy decides`, amount: eur(tot(target)), tone: 'neutral', notes: [imCopy ? imCopy.rail.node3 : 'target shelf price'] })

	// 4 — the conversion, written out so it can be checked
	if (factor != null) {
		steps.push({ type: 'step', operator: `${eur(tot(target))} ÷ ${factor.toFixed(4)} = ${eur(tot(netBeforeCaps))}`, note: conversionNote(markup, d?.limits?.markup) })
	}

	steps.push({ type: 'node', label: `${n()} · Price to set in the PMS`, amount: eur(tot(netBeforeCaps)), tone: 'neutral', notes: ['what the customer will pay'] })

	for (const c of netCaps) steps.push(capNode(c, false))

	steps.push({
		type: 'result',
		amount: days ? `${eur(tot(result))} · ${eur(result)}/day` : eur(result),
		was: row.previous_price != null ? `was ${eur(tot(row.previous_price))}` : null,
		delta: delta != null ? Number(tot(delta)) : null,
		simulated: !!row.simulated,
		// The result is the decision; whether it reached the PMS is the write log's to say (CU-869f03vek).
		unwritten: unwrittenNote(row),
	})

	return { kind: 'market', caption, steps, predates: !d }
}

/**
 * A tethered child's chain: its parent's price plus the spread the tether declares — never a market.
 * `{ kind: 'tether', caption, steps }`, as `TetherDerivation` draws it.
 */
export function tetherSteps(row) {
	if (!row) return null
	const vt = row.via_tether
	const pct = vt?.delta?.k === 'pct'
	const v = vt?.delta?.v || 0
	const result = row.new_price ?? row.pushed_price
	const spread = pct
		? `× ${(1 + v / 100).toFixed(3)} — spread ${v > 0 ? '+' : ''}${v} %`
		: `${v >= 0 ? '+' : '−'} ${eur(Math.abs(v))} — spread`
	return {
		kind: 'tether',
		caption: `Tether · ${vt?.tether_name || vt?.dimension}`,
		steps: [
			{ type: 'node', label: '1 · The parent', amount: eur(vt?.parent_price), tone: 'neutral',
			  notes: [`${dimensionLabel(vt?.dimension)} ${tetherSideLabel(vt, 'parent')} — priced on its own market`] },
			{ type: 'step', operator: `${eur(vt?.parent_price)} ${spread} = ${eur(result)}`,
			  note: `${tetherSideLabel(vt, 'parent')} → ${tetherSideLabel(vt, 'child')}` },
			{ type: 'node', label: '2 · Price to set in the PMS', amount: eur(result), tone: 'neutral',
			  notes: ['what the customer will pay · inherited, never capped'] },
			{ type: 'node', label: 'Result', amount: eur(result), tone: 'strong', last: true,
			  notes: row.simulated ? ['dry run — nothing written'] : unwrittenNote(row) ? [unwrittenNote(row)] : [] },
		],
	}
}
